import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/common/PageHeader';
import { fetchPartnerDashboard, rotatePartnerBenefitLink } from 'helpers/partners';

const getBenefitLabelKey = option => {
  switch (option) {
    case 'discount_only':
      return 'PARTNERS.BENEFIT_OPTIONS.DISCOUNT_ONLY';
    case 'commission_only':
      return 'PARTNERS.BENEFIT_OPTIONS.COMMISSION_ONLY';
    case 'split':
      return 'PARTNERS.BENEFIT_OPTIONS.SPLIT';
    default:
      return 'PARTNERS.BENEFIT_OPTIONS.UNKNOWN';
  }
};

const getBenefitHelpKey = option => {
  switch (option) {
    case 'discount_only':
      return 'PARTNERS.BENEFIT_OPTIONS.DISCOUNT_ONLY_HELP';
    case 'commission_only':
      return 'PARTNERS.BENEFIT_OPTIONS.COMMISSION_ONLY_HELP';
    case 'split':
      return 'PARTNERS.BENEFIT_OPTIONS.SPLIT_HELP';
    default:
      return 'PARTNERS.BENEFIT_OPTIONS.UNKNOWN_HELP';
  }
};

const PartnersDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rotatingKey, setRotatingKey] = useState('');
  const statCards = [
    { key: 'applications_count', title: t('PARTNERS.STATS.APPLICATIONS') },
    { key: 'joined_count', title: t('PARTNERS.STATS.JOINED') },
    { key: 'pending_count', title: t('PARTNERS.STATS.PENDING') }
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchPartnerDashboard();
        setData(response);
      } catch (requestError) {
        setError(requestError.response?.data?.error || t('PARTNERS.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [t]);

  const signupLink = data?.partner?.signup_link
    ? `${window.location.origin}${data.partner.signup_link}`
    : '';
  const benefitLinks = data?.partner?.benefit_links || [];

  const copyLink = async linkPath => {
    const fullLink = linkPath ? `${window.location.origin}${linkPath}` : '';
    if (!fullLink) return;
    await navigator.clipboard.writeText(fullLink);
  };

  const handleRotateLink = async benefitKey => {
    setRotatingKey(benefitKey);
    setError('');
    try {
      await rotatePartnerBenefitLink(benefitKey);
      const response = await fetchPartnerDashboard();
      setData(response);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('PARTNERS.ERROR_LOAD'));
    } finally {
      setRotatingKey('');
    }
  };

  if (loading) {
    return <div className="d-flex justify-content-center py-6"><Spinner animation="border" /></div>;
  }

  return (
    <>
      <PageHeader
        title={data?.partner?.label || t('PARTNERS.TITLE')}
        description={t('PARTNERS.DESCRIPTION')}
        className="mb-4"
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3 mb-4">
        {statCards.map(card => (
          <Col md={4} key={card.key}>
            <Card className="h-100 border">
              <Card.Body>
                <div className="text-600 fs--1 mb-2">{card.title}</div>
                <div className="fs-2 fw-bold">{data?.stats?.[card.key] || 0}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border">
        <Card.Body>
          <div className="mb-3">
            <h5 className="mb-2">{t('PARTNERS.LINK_TITLE')}</h5>
            <div className="text-600 fs--1">{t('PARTNERS.CODE', { code: data?.partner?.code || '-' })}</div>
          </div>
          {benefitLinks.length ? (
            <Row className="g-3">
              {benefitLinks.map(link => {
                const fullLink = link?.signup_link ? `${window.location.origin}${link.signup_link}` : '';
                return (
                  <Col md={4} key={link.key}>
                    <Card className="h-100 border bg-light">
                      <Card.Body className="d-flex flex-column gap-2">
                        <div className="fw-semibold">{t(getBenefitLabelKey(link.key))}</div>
                        <div className="text-600 fs--1">{t(getBenefitHelpKey(link.key))}</div>
                        <div className="small">
                          <strong>{t('PARTNERS.COMMISSION_PCT')}:</strong> {link.commission_pct}%
                        </div>
                        <div className="small">
                          <strong>{t('PARTNERS.CUSTOMER_DISCOUNT_PCT')}:</strong> {link.customer_discount_pct}%
                        </div>
                        <div className="text-break text-700 small">{fullLink}</div>
                        <div className="mt-auto d-flex gap-2 flex-wrap">
                          <Button onClick={() => copyLink(link.signup_link)} disabled={!fullLink}>
                            {t('PARTNERS.COPY_LINK')}
                          </Button>
                          <Button
                            variant="outline-warning"
                            onClick={() => handleRotateLink(link.key)}
                            disabled={rotatingKey === link.key}
                          >
                            {rotatingKey === link.key
                              ? <Spinner size="sm" animation="border" />
                              : t('PARTNERS.GENERATE_NEW_LINK')}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div className="text-700">{signupLink || t('PARTNERS.LINK_UNAVAILABLE')}</div>
              <Button onClick={() => copyLink(data?.partner?.signup_link)} disabled={!signupLink}>
                {t('PARTNERS.COPY_LINK')}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default PartnersDashboard;
